import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingTarget,
  useDigitalTwinSettings,
} from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { useGetAllEventMutation } from 'api/events/events.api'
import { useEffect, useState } from 'react'
import { getSettingsWithUnitsFromMatchingConfigurableProperty } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { EventPost } from 'views/EventsView/components/events.helper'

type PropertySettingsParams = {
  digitalTwin: DigitalTwin
  configurableProperties: ConfigurableProperty[]
  property: DigitalTwinSettingTarget
  priorities: SystemSettingPriorityLevel[]
  /** Refetches the settings whenever it changes. */
  refetchKey: number
}

type PropertySettings = {
  settings: DigitalTwinSetting[]
  /** Until the property's settings have been fetched once; refetches keep the settings already shown. */
  isPending: boolean
  operationalEvents: Map<number, EventPost>
  refetchOperationalEvents: () => void
}

function indexEventsById(events: EventPost[]): Map<number, EventPost> {
  const eventsById = new Map<number, EventPost>()
  events.forEach((event) => {
    if (event.id !== undefined) {
      eventsById.set(event.id, event)
    }
  })
  return eventsById
}

/** Every setting on one property — active, upcoming and previous — and the events they are linked to. */
export function usePropertySettings({
  digitalTwin,
  configurableProperties,
  property: { level, name, attribute },
  priorities,
  refetchKey,
}: PropertySettingsParams): PropertySettings {
  const [settings, setSettings] = useState<DigitalTwinSetting[]>([])
  const [hasFetchedSettings, setHasFetchedSettings] = useState(false)
  const [operationalEvents, setOperationalEvents] = useState<Map<number, EventPost>>(new Map())
  const [eventsRequestCount, setEventsRequestCount] = useState(0)
  const { mutateAsync: fetchSettings } = useDigitalTwinSettings()
  const { mutateAsync: fetchOperationalEvents } = useGetAllEventMutation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchKey is the refetch trigger
  useEffect(() => {
    const properties = configurableProperties.filter(
      (property) => property.level === level && property.name === name && property.attribute === attribute
    )
    if (properties.length === 0) {
      return
    }

    let isCurrent = true
    fetchSettings({ digitalTwinUid: digitalTwin.uid, properties, priorities }).then((fetched) => {
      if (isCurrent) {
        setSettings(fetched)
        setHasFetchedSettings(true)
      }
    })
    return () => {
      isCurrent = false
    }
  }, [digitalTwin.uid, configurableProperties, level, name, attribute, priorities, fetchSettings, refetchKey])

  // biome-ignore lint/correctness/useExhaustiveDependencies: eventsRequestCount is the refetch trigger
  useEffect(() => {
    if (!settings.some((setting) => setting.operational_event !== null)) {
      return
    }

    let isCurrent = true
    fetchOperationalEvents({ object_category: level, object_name: name, attribute }).then((events) => {
      if (isCurrent) {
        setOperationalEvents(indexEventsById(events))
      }
    })
    return () => {
      isCurrent = false
    }
  }, [settings, level, name, attribute, fetchOperationalEvents, eventsRequestCount])

  const isConfigurable = configurableProperties.some(
    (property) => property.level === level && property.name === name && property.attribute === attribute
  )

  return {
    settings: getSettingsWithUnitsFromMatchingConfigurableProperty(digitalTwin, settings),
    isPending: isConfigurable && !hasFetchedSettings,
    operationalEvents,
    refetchOperationalEvents: () => setEventsRequestCount((count) => count + 1),
  }
}
